import { BorderStyle, BorderThickness, DynamicCssModel } from "../../../models/DynamicCssModel";
import {
    getLegacyGridDynamicCssScope,
    isDuoChromeTabBackground,
} from "../../../theme/gridThemeTokens";
import { alphaByPercentage, foregroundColor } from "../../../theme/Mixins";
import type { Theme } from "../../../types/Theme";

export function generateTableRowColors(
    dynamicCss: DynamicCssModel,
    theme: object,
): {
    primaryBgColor: string;
    secondaryBgColor: string;
    primaryColor: string;
    secondaryColor: string;
    hoverColor: string;
    hoverBgColor: string;
    selectedColor: string;
    selectedBgColor: string;
} {
    const primaryBgColor = alphaByPercentage(
        theme[dynamicCss.TableRowDefaultTabFillPrimaryColor],
        dynamicCss.TableRowDefaultTabFillPrimaryColorPercent,
    );

    const secondaryBgColor = alphaByPercentage(
        theme[dynamicCss.TableRowDefaultTabFillSecondaryColor],
        dynamicCss.TableRowDefaultTabFillSecondaryColorPercent,
    );

    const hoverTabColor = theme[dynamicCss.TableRowHoverTabColor];

    const hoverBgColor = hoverTabColor
        ? alphaByPercentage(hoverTabColor, dynamicCss.TableRowHoverTabColorPercent)
        : null;
    const selectedBgColor = alphaByPercentage(
        theme[dynamicCss.TableRowSelectedTabColor],
        dynamicCss.TableRowSelectedTabColorPercent,
    );

    const primaryColor = foregroundColor(theme, primaryBgColor);

    const secondaryColor = foregroundColor(theme, secondaryBgColor);
    return {
        primaryBgColor,
        secondaryBgColor,
        primaryColor,
        secondaryColor,
        hoverBgColor,
        hoverColor: foregroundColor(theme, hoverBgColor),
        selectedBgColor,
        selectedColor: foregroundColor(theme, selectedBgColor),
    };
}

export function getBorder(theme: object, dynamicCss: DynamicCssModel) {
    return `${
        dynamicCss.TableRowBorderThickness === BorderThickness.BoldSolid ? "3px" : "1px"
    } solid ${alphaByPercentage(
        theme[dynamicCss.TableRowBorderColor],
        dynamicCss.TableRowBorderColorPercent,
    )} !important`;
}

export function createCssForGridBorder(theme: object, dynamicCss: DynamicCssModel): string {
    // eslint-disable-next-line default-case
    let css = "";
    const border = getBorder(theme, dynamicCss);
    const legacyGrid = getLegacyGridDynamicCssScope(theme as Theme);
    if (BorderStyle.Outside === dynamicCss.TableRowBorderStyle) {
        css += `
        ${legacyGrid} tr.data-row { border-left: ${border}; border-right: ${border}; }
        ${legacyGrid} .header-row + .data-row { border-top: ${border}; }
        ${legacyGrid} tr.data-row:last-child { border-bottom: ${border}; }
        `;
    }

    if (BorderStyle.Vertical === dynamicCss.TableRowBorderStyle) {
        css += `${legacyGrid} tr.data-row td:not(:last-child), ${legacyGrid} tr.data-row th.grid__row-header { 
            border: none;
            border-right: ${border}; 
        }`;
    }

    if (BorderStyle.Horizontal === dynamicCss.TableRowBorderStyle) {
        css += `${legacyGrid} tr.data-row:not(:last-child) td, ${legacyGrid} tr.data-row:not(:last-child) th.grid__row-header { 
            border: none;
            border-bottom: ${border}; 
        }`;
    }

    if (BorderStyle.All === dynamicCss.TableRowBorderStyle) {
        css += `${legacyGrid} { border: ${border}; }
        ${legacyGrid} th, ${legacyGrid} td { border: ${border}; }`;
    }

    return css;
}

export function createCssForGridDataRowBackground(
    theme: object,
    dynamicCss: DynamicCssModel,
    primaryColor: string,
    secondaryColor: string,
    primaryBg: string,
    secondaryBg: string,
    hoverColor: string,
    hoverBg: string,
    selectedColor: string,
    selectedBg: string,
): string {
    const legacyGrid = getLegacyGridDynamicCssScope(theme as Theme);
    const isDuoChrome = isDuoChromeTabBackground(dynamicCss.TableRowDefaultTabBackground);
    // The zebra rules are just as !important but carry the `legacyGrid` scope, so a bare
    // `.grid-view` selector loses to them. The scoped selector ties their specificity and wins
    // on source order; the bare one keeps the fill on modern lookup tables, outside that scope.
    const selectedRow = `.grid-view tr.data-row.row--selected, ${legacyGrid} tr.data-row.row--selected`;
    let hoverStyles = "";
    if (hoverBg) {
        hoverStyles = `
        ${legacyGrid} tr.data-row:hover { 
            background-color: ${hoverBg} !important; 
            color: ${hoverColor} !important; 
        }`;
    }

    return `
        .grid-view:not(.grid-view--star-rating) {
            width: 100%;
            border-collapse: collapse !important;
            margin: 0px !important;
        }
        .grid-view tr.data-row > :first-child { border-left:none !important; } 
        .grid-view tr.data-row > :last-child { border-right:none !important; }
        ${legacyGrid} tr.data-row:nth-child(even) { 
            background-color: ${isDuoChrome ? secondaryBg : primaryBg} !important; 
            color: ${isDuoChrome ? secondaryColor : primaryColor} !important; 
        }
        ${legacyGrid} tr.data-row:nth-child(odd) { background-color: ${primaryBg} !important;  color: ${primaryColor} !important; }
        ${selectedRow} { background-color: ${selectedBg} !important; color: ${selectedColor} !important; }
        ${legacyGrid} tr.data-row th,${legacyGrid} tr.data-row td { background-color:transparent; color:inherit !important;}
        ${hoverStyles}    
        `;
}
